/**
 * Workspace-roster search for the speaker picker.
 *
 * An empty query (the field focused, nothing typed) loads the first page of
 * people in the workspace — the API's own order, limit 20 — with no debounce,
 * so the list is there on focus. Typing debounces 200 ms and sends that query.
 * React Query keys the cache by the trimmed string, so every open picker shares
 * one empty-roster request.
 */

import { useDebouncedValue } from "@mantine/hooks"
import { fetchCompanySpeakers, type ICompanySpeaker } from "../lib/api"
import { queryKeys } from "../lib/query-keys"
import { useCompanyQuery } from "./useCompanyQuery"

export interface ISpeakerRosterSearch {
  people: ICompanySpeaker[]
  isFetching: boolean
}

export function useSpeakerRosterSearch(query: string, enabled: boolean): ISpeakerRosterSearch {
  const [debounced] = useDebouncedValue(query, 200)
  // Empty focus skips the debounce so the roster is visible immediately.
  // A non-empty box waits, and until it settles the last empty result stays
  // cached for the picker to filter on the client.
  const trimmed = query.trim().length === 0 ? "" : debounced.trim()
  const { data, isFetching } = useCompanyQuery({
    key: (orgId) => queryKeys.companySpeakers(orgId, trimmed),
    queryFn: (company) => fetchCompanySpeakers(company, { q: trimmed, limit: 20 }),
    enabled,
  })

  return {
    people: data ?? [],
    isFetching: enabled && isFetching,
  }
}
